import { DEFAULT_UI_LOCALE, fillTemplate, isUiLocale, NOTIFICATION_TEXTS, type NotificationParams } from './i18n';
import { languageName } from './languages';
import type { NotificationType } from './types';

/** Renders a notification in the reader's interface language. Used for Discord DMs and the in-app bell. */
export function renderNotification(type: NotificationType, params: NotificationParams, locale: string): string {
  const texts = NOTIFICATION_TEXTS[isUiLocale(locale) ? locale : DEFAULT_UI_LOCALE];
  return fillTemplate(texts[type], {
    ...params,
    language: params.language ? languageName(params.language, locale) : undefined,
  });
}

export function openLinkLabel(locale: string): string {
  return NOTIFICATION_TEXTS[isUiLocale(locale) ? locale : DEFAULT_UI_LOCALE].open;
}

export interface RenderedEmail {
  subject: string;
  text: string;
  html: string;
}

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/**
 * A notification email in the reader's language. `originalNote` is the untranslated note, shown
 * quoted under the translation when they differ.
 */
export function renderEmail(
  type: NotificationType,
  params: NotificationParams,
  locale: string,
  url: string,
  originalNote?: string,
): RenderedEmail {
  const code = isUiLocale(locale) ? locale : DEFAULT_UI_LOCALE;
  const texts = NOTIFICATION_TEXTS[code];
  const message = renderNotification(type, params, code);
  const subject =
    type === 'changes_requested'
      ? fillTemplate(texts.changes_requested_subject, {
          ...params,
          language: params.language ? languageName(params.language, code) : undefined,
        })
      : message;
  const quoted = originalNote && originalNote !== params.note ? originalNote : undefined;

  const text = [message, quoted ? `> ${quoted.replace(/\n/g, '\n> ')}` : '', `${texts.open}: ${url}`, '', '—', texts.footer]
    .filter((line, i) => line !== '' || i > 2)
    .join('\n');

  const dir = code === 'ar' ? 'rtl' : 'ltr';
  const html = `<!doctype html>
<html lang="${code}" dir="${dir}"><body style="margin:0;padding:24px;background:#f6f5fb;font-family:Inter,system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;color:#1b1a23">
<div style="max-width:560px;margin:0 auto;background:#ffffff;border:1px solid #e4e2ee;border-radius:12px;padding:24px">
<p style="margin:0 0 16px;font-weight:700;color:#7c5cff">DubForge</p>
<p style="margin:0 0 16px;font-size:16px;line-height:1.5;white-space:pre-wrap">${escapeHtml(message)}</p>
${quoted ? `<blockquote style="margin:0 0 16px;padding:8px 12px;border-inline-start:3px solid #e4e2ee;color:#5f5d70;white-space:pre-wrap">${escapeHtml(quoted)}</blockquote>` : ''}
<p style="margin:24px 0"><a href="${escapeHtml(url)}" style="display:inline-block;background:#7c5cff;color:#ffffff;text-decoration:none;font-weight:600;padding:10px 18px;border-radius:8px">${escapeHtml(texts.open)}</a></p>
<p style="margin:24px 0 0;font-size:12px;line-height:1.5;color:#5f5d70">${escapeHtml(texts.footer)}</p>
</div></body></html>`;

  return { subject: subject.slice(0, 200), text, html };
}
